import { supabase } from '../lib/supabase';

export const sessionService = {
  // Get all sessions for current user, ordered by date ascending
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('training_sessions')?.select('*')?.eq('user_id', user?.id)?.order('date', { ascending: true });

    if (error) throw error;

    // Convert snake_case to camelCase for React
    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      date: row?.date,
      minutes: row?.minutes,
      activityType: row?.activity_type, // NEW: Map activity_type
      activeTime: row?.active_time, // NEW: Map active_time
      rpe: row?.rpe,
      load: row?.load,
      notes: row?.notes || '',
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Create new session
  async create(sessionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case for database
    const { data, error } = await supabase?.from('training_sessions')?.insert({
        user_id: user?.id,
        date: sessionData?.date,
        minutes: sessionData?.minutes || sessionData?.duration || sessionData?.totalDuration, // Support multiple field names
        activity_type: sessionData?.activityType || sessionData?.sport, // NEW: Map sport/activityType to activity_type
        active_time: sessionData?.activeTime || sessionData?.activeDuration, // NEW: Map activeDuration to active_time
        rpe: sessionData?.rpe,
        load: sessionData?.load || (sessionData?.activeTime || sessionData?.activeDuration || sessionData?.minutes || sessionData?.duration || sessionData?.totalDuration) * sessionData?.rpe,
        notes: sessionData?.notes || ''
      })?.select()?.single();

    if (error) {
      if (error?.code === '23505') {
        throw new Error('A session with the same date, minutes, and RPE already exists');
      }
      throw error;
    }

    // Convert back to camelCase
    return {
      id: data?.id,
      userId: data?.user_id,
      date: data?.date,
      minutes: data?.minutes,
      activityType: data?.activity_type, // NEW
      activeTime: data?.active_time, // NEW
      rpe: data?.rpe,
      load: data?.load,
      notes: data?.notes || '',
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update session
  async update(sessionId, sessionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await // Ensure user can only update their own sessions
    supabase?.from('training_sessions')?.update({
        date: sessionData?.date,
        minutes: sessionData?.minutes || sessionData?.duration || sessionData?.totalDuration,
        activity_type: sessionData?.activityType || sessionData?.sport, // NEW
        active_time: sessionData?.activeTime || sessionData?.activeDuration, // NEW
        rpe: sessionData?.rpe,
        load: sessionData?.load || (sessionData?.activeTime || sessionData?.activeDuration || sessionData?.minutes || sessionData?.duration || sessionData?.totalDuration) * sessionData?.rpe,
        notes: sessionData?.notes || ''
      })?.eq('id', sessionId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) {
      if (error?.code === '23505') {
        throw new Error('A session with the same date, minutes, and RPE already exists');
      }
      throw error;
    }

    return {
      id: data?.id,
      userId: data?.user_id,
      date: data?.date,
      minutes: data?.minutes,
      activityType: data?.activity_type, // NEW
      activeTime: data?.active_time, // NEW
      rpe: data?.rpe,
      load: data?.load,
      notes: data?.notes || '',
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete session
  async delete(sessionId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('training_sessions')?.delete()?.eq('id', sessionId)?.eq('user_id', user?.id); // Ensure user can only delete their own sessions

    if (error) throw error;
  },

  // Delete all sessions for current user
  async deleteAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('training_sessions')?.delete()?.eq('user_id', user?.id);

    if (error) throw error;
  },

  // Export sessions to CSV with required template format
  async exportToCSV() {
    const sessions = await this.getAll();
    
    // CSV header template: Updated to include activity_type and active_time
    const csvHeaders = ['date', 'activity_type', 'minutes', 'active_time', 'rpe', 'load', 'notes'];
    
    // Convert sessions to CSV format - use ISO-8601 date format YYYY-MM-DD
    const csvData = sessions?.map(session => [
      session?.date, // Already in YYYY-MM-DD format from database
      session?.activityType || '', // NEW: Activity type
      session?.minutes, // Total duration
      session?.activeTime || session?.minutes, // NEW: Active time (fallback to minutes if not set)
      session?.rpe,
      session?.load,
      `"${(session?.notes || '')?.replace(/"/g, '""')}"` // Escape quotes in notes
    ]);

    // Create CSV content
    const csvContent = [
      csvHeaders?.join(','),
      ...csvData?.map(row => row?.join(','))
    ]?.join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-sessions-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);

    return sessions?.length; // Return count of exported sessions
  },

  // Import sessions from CSV with validation
  async importFromCSV(csvFile) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const csvText = event.target.result;
          const lines = csvText.split('\n').filter(line => line.trim());
          
          if (lines.length === 0) {
            reject(new Error('Empty CSV file'));
            return;
          }

          // Validate header format (support both old and new formats)
          const expectedHeadersOld = ['date', 'minutes', 'rpe', 'load', 'notes'];
          const expectedHeadersNew = ['date', 'activity_type', 'minutes', 'active_time', 'rpe', 'load', 'notes'];
          const actualHeaders = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
          
          const isOldFormat = JSON.stringify(actualHeaders) === JSON.stringify(expectedHeadersOld);
          const isNewFormat = JSON.stringify(actualHeaders) === JSON.stringify(expectedHeadersNew);
          
          if (!isOldFormat && !isNewFormat) {
            reject(new Error(`Invalid CSV header. Expected either:\nOld format: ${expectedHeadersOld.join(', ')}\nNew format: ${expectedHeadersNew.join(', ')}\nGot: ${actualHeaders.join(', ')}`));
            return;
          }

          // Parse data rows
          const dataRows = lines.slice(1);
          const sessions = [];
          const errors = [];
          let duplicateCount = 0;

          for (let i = 0; i < dataRows.length; i++) {
            const line = dataRows[i].trim();
            if (!line) continue;

            try {
              // Parse CSV line (handle quoted notes)
              const columns = this.parseCSVLine(line);
              
              const expectedColumns = isNewFormat ? 7 : 5;
              if (columns.length !== expectedColumns) {
                errors.push(`Row ${i + 2}: Expected ${expectedColumns} columns, got ${columns.length}`);
                continue;
              }

              let dateStr = '';
              let activityTypeStr = '';
              let minutesStr = '';
              let activeTimeStr = '';
              let rpeStr = '';
              let loadStr = '';
              let notesStr = '';
              
              if (isNewFormat) {
                [dateStr, activityTypeStr, minutesStr, activeTimeStr, rpeStr, loadStr, notesStr] = columns;
              } else {
                // Old format - no activity_type or active_time
                [dateStr, minutesStr, rpeStr, loadStr, notesStr] = columns;
                activityTypeStr = '';
                activeTimeStr = minutesStr; // Use minutes as active_time for old format
              }

              // Validate and parse date - support multiple formats and auto-convert to YYYY-MM-DD
              let date;
              
              if (this.isValidDate(dateStr)) {
                date = this.convertToISODate(dateStr);
                if (!date) {
                  errors.push(`Row ${i + 2}: Invalid date "${dateStr}". Supported formats: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, MM-DD-YYYY`);
                  continue;
                }
              } else {
                errors.push(`Row ${i + 2}: Invalid date "${dateStr}". Supported formats: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, MM-DD-YYYY`);
                continue;
              }

              // Validate activity type (optional)
              const activityType = activityTypeStr?.trim() || null;

              // Validate and parse minutes (total duration)
              const minutes = parseInt(minutesStr);
              if (isNaN(minutes) || minutes < 0) {
                errors.push(`Row ${i + 2}: Invalid minutes "${minutesStr}". Must be positive number or zero`);
                continue;
              }

              // Validate and parse active_time
              const activeTime = parseInt(activeTimeStr);
              if (isNaN(activeTime) || activeTime < 0) {
                errors.push(`Row ${i + 2}: Invalid active_time "${activeTimeStr}". Must be positive number or zero`);
                continue;
              }

              // Validate that active_time doesn't exceed total minutes
              if (activeTime > minutes) {
                errors.push(`Row ${i + 2}: Active time (${activeTime}) cannot exceed total duration (${minutes})`);
                continue;
              }

              // Validate and parse RPE
              const rpe = parseInt(rpeStr);
              if (isNaN(rpe) || rpe < 1 || rpe > 10) {
                errors.push(`Row ${i + 2}: Invalid RPE "${rpeStr}". Must be between 1-10`);
                continue;
              }

              // Parse load (calculate if missing or invalid)
              let load = parseInt(loadStr);
              if (isNaN(load) || load <= 0) {
                load = activeTime * rpe; // Calculate load = active_time × rpe
              }

              // Parse notes (remove quotes)
              const notes = notesStr.replace(/^"(.*)"$/, '$1').replace(/""/g, '"');

              sessions.push({
                date,
                activityType,
                minutes,
                activeTime,
                rpe,
                load,
                notes
              });

            } catch (error) {
              errors.push(`Row ${i + 2}: ${error.message}`);
            }
          }

          // If there are validation errors, return them
          if (errors.length > 0) {
            reject(new Error(`Validation errors:\n${errors.join('\n')}`));
            return;
          }

          // Import sessions (ignore duplicates, count warnings)
          const importedSessions = [];
          
          for (const sessionData of sessions) {
            try {
              const imported = await this.create(sessionData);
              importedSessions.push(imported);
            } catch (error) {
              if (error.message.includes('already exists')) {
                duplicateCount++;
              } else {
                errors.push(`Failed to import session ${sessionData.date}: ${error.message}`);
              }
            }
          }

          resolve({
            imported: importedSessions,
            duplicates: duplicateCount,
            errors: errors,
            total: sessions.length
          });

        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(csvFile);
    });
  },

  // Helper function to parse CSV line with quoted fields
  parseCSVLine(line) {
    const columns = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line?.length; i++) {
      const char = line?.[i];
      
      if (char === '"') {
        if (inQuotes && line?.[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of column
        columns?.push(current?.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last column
    columns?.push(current?.trim());
    
    return columns;
  },

  // Helper function to validate if a string could be a date
  isValidDate(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return false;
    
    // Support multiple date formats
    const formats = [
      /^\d{4}-\d{2}-\d{2}$/,        // YYYY-MM-DD (ISO format)
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,  // DD/MM/YYYY or MM/DD/YYYY
      /^\d{1,2}-\d{1,2}-\d{4}$/,    // DD-MM-YYYY or MM-DD-YYYY
    ];
    
    return formats?.some(format => format?.test(dateStr?.trim()));
  },

  // Helper function to convert various date formats to YYYY-MM-DD
  convertToISODate(dateStr) {
    const trimmed = dateStr?.trim();
    
    // Already in ISO format YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/?.test(trimmed)) {
      const testDate = new Date(trimmed);
      return !isNaN(testDate?.getTime()) ? trimmed : null;
    }
    
    // Handle DD/MM/YYYY or MM/DD/YYYY formats
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/?.test(trimmed)) {
      const [part1, part2, year] = trimmed?.split('/');
      const day = parseInt(part1);
      const month = parseInt(part2);
      
      // Determine if it's DD/MM/YYYY or MM/DD/YYYY based on values
      let isoDate;
      if (day > 12) {
        // Must be DD/MM/YYYY format (day > 12)
        isoDate = `${year}-${month?.toString()?.padStart(2, '0')}-${day?.toString()?.padStart(2, '0')}`;
      } else if (month > 12) {
        // Must be MM/DD/YYYY format (month > 12, so part2 is day)
        isoDate = `${year}-${day?.toString()?.padStart(2, '0')}-${month?.toString()?.padStart(2, '0')}`;
      } else {
        // Ambiguous case - assume DD/MM/YYYY (more common internationally)
        isoDate = `${year}-${month?.toString()?.padStart(2, '0')}-${day?.toString()?.padStart(2, '0')}`;
      }
      
      // Validate the resulting date
      const testDate = new Date(isoDate);
      return !isNaN(testDate?.getTime()) ? isoDate : null;
    }
    
    // Handle DD-MM-YYYY or MM-DD-YYYY formats
    if (/^\d{1,2}-\d{1,2}-\d{4}$/?.test(trimmed)) {
      const [part1, part2, year] = trimmed?.split('-');
      const day = parseInt(part1);
      const month = parseInt(part2);
      
      // Determine if it's DD-MM-YYYY or MM-DD-YYYY based on values
      let isoDate;
      if (day > 12) {
        // Must be DD-MM-YYYY format (day > 12)
        isoDate = `${year}-${month?.toString()?.padStart(2, '0')}-${day?.toString()?.padStart(2, '0')}`;
      } else if (month > 12) {
        // Must be MM-DD-YYYY format (month > 12, so part2 is day)
        isoDate = `${year}-${day?.toString()?.padStart(2, '0')}-${month?.toString()?.padStart(2, '0')}`;
      } else {
        // Ambiguous case - assume DD-MM-YYYY (more common internationally)
        isoDate = `${year}-${month?.toString()?.padStart(2, '0')}-${day?.toString()?.padStart(2, '0')}`;
      }
      
      // Validate the resulting date
      const testDate = new Date(isoDate);
      return !isNaN(testDate?.getTime()) ? isoDate : null;
    }
    
    return null;
  }
};